import type { ModuleRecord } from '@/types/domain';

/**
 * 问题升级层级（前端这份只管显示与建议标记；判据的权威在后端
 * apps/api/src/modules/issue-escalation.ts，两边的文字和阈值要一致）。
 *
 * LOP G 列的四级：1 工程师(灰) → 2 项目经理与核心组(绿) → 3 部门负责人(黄) → 4 管理层(红)。
 * 界面以「谁在扛」为主标签、颜色为辅——第 2 级是绿色，但「绿色问题」会被当成没事。
 */
export type IssueLevel = 1 | 2 | 3 | 4;

export const ISSUE_LEVELS: ReadonlyArray<{ level: IssueLevel; label: string; short: string; tone: string }> = [
  { level: 1, label: '工程师', short: '工程师', tone: 'grey' },
  { level: 2, label: '项目经理与核心组', short: '项目经理', tone: 'green' },
  { level: 3, label: '部门负责人', short: '部门负责人', tone: 'yellow' },
  { level: 4, label: '管理层', short: '管理层', tone: 'red' },
];

export function issueLevelOf(value: unknown): IssueLevel {
  const n = Number(value);
  if (n >= 4) return 4;
  if (n === 3) return 3;
  if (n === 2) return 2;
  return 1;
}

export function issueLevelLabel(value: unknown, short = false): string {
  const found = ISSUE_LEVELS.find((item) => item.level === issueLevelOf(value));
  return (short ? found?.short : found?.label) ?? '工程师';
}

export function issueLevelTone(value: unknown): string {
  return ISSUE_LEVELS.find((item) => item.level === issueLevelOf(value))?.tone ?? 'grey';
}

/** 和后端 SUGGEST_AFTER_DAYS / SUGGEST_AFTER_REMINDERS 同值 */
export const SUGGEST_AFTER_DAYS = 7;
export const SUGGEST_AFTER_REMINDERS = 2;

/**
 * 「建议升级」：逾期满 7 天，或升到这一级之后被催过 2 次没动。
 * 系统只建议、人拍板；已经在管理层或已关闭的不建议。
 */
export function escalationAdvice(input: {
  level: unknown; open: boolean; planDate: string; today: string; remindersSinceLevel: number;
}): string {
  if (!input.open || issueLevelOf(input.level) >= 4) return '';
  const parts: string[] = [];
  const plan = input.planDate.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/u.test(plan)) {
    const days = Math.floor((Date.parse(`${input.today}T00:00:00Z`) - Date.parse(`${plan}T00:00:00Z`)) / 86_400_000);
    if (days >= SUGGEST_AFTER_DAYS) parts.push(`逾期 ${days} 天`);
  }
  if (input.remindersSinceLevel >= SUGGEST_AFTER_REMINDERS) parts.push(`催过 ${input.remindersSinceLevel} 次没动`);
  return parts.join('，');
}

/** 升降履历的来源怎么叫（弹窗和看板共用一份） */
export function escalationSourceLabel(source: string): string {
  const labels: Record<string, string> = {
    manual: '人工',
    auto: '系统自动',
    grade_floor: 'A 级地板',
    complaint_floor: '客户升级投诉',
    backfill: '上线回填',
  };
  return labels[source] ?? source;
}

/** 从一条问题记录上读层级（列表和单条都带 escalation_level） */
export function recordLevel(item: ModuleRecord): IssueLevel {
  return issueLevelOf(item.escalation_level);
}
